import { useHierarchyArea } from '@plarium/hierarchy-react';
import React, { useMemo } from 'react';

import { Grid, Alert, Section, Checkbox } from '@gogame/components';
import { withForm, FormField, SidePageV2 } from '@gogame/form';

import {
  NAME,
  NUMBER,
  APPLICATION,
  CAMPAIGNS,
  ACTIVITY_TYPE,
  PLATFORMS,
  START_DATE,
  FINISH_DATE,
  PROMO_CODE,
  AFFILIATE,
  REWARDS,
  DEPENDENCIES,
  STATUS_ACTIVE,
  STATUS_EXPIRED,
  STATUS_INACTIVE,
  REQUESTED_BY,
  COMMENT,
  JIRA_LINK,
  COUNTRIES,
  EXCLUDED_COUNTRIES,
  INCLUDE_RE_ATTR,
  CONDITIONS,
  MULTI_REDEMPTION,
  MULTI_REDEMPTION_ACTIVITY_PERIOD,
  MULTI_REDEMPTION_COOLDOWN_PERIOD,
  MAX_REDEMPTIONS_PER_USER,
} from 'scenes/PromoCodes/constants';
import { useValidatePromoCode } from 'scenes/PromoCodes/hooks';
import {
  loadApplications,
  loadCampaigns,
  loadTypes,
  loadRewards,
  loadAffiliate,
  loadUsers,
  loadPlatforms,
  loadCountries,
  loadConditions,
} from 'scenes/PromoCodes/sources/fields';

import ConditionalRewards from './ConditionalRewards';
import Rewards from './Rewards';
import { MultiChangeSelect, DeactivateUponSelect } from './components';

const getOptionNameAndId = ({ name, id }) => `${name} (${id})`;

const PromoCodeForm = withForm({
  enableReinitialize: true,
})(({
  values,
  onClose,
  title,
  subTitle,
  loading,
  validateField,
  setValues,
  hierarchyState,
  onExited,
  open,
  isSetFieldVisible,
  isPublicFieldVisible,
  active,
  entityId,
}) => {
  const validatePromoCode = useValidatePromoCode();

  const currentStatus = values?.status?.id;
  const disabledField = [
    STATUS_ACTIVE,
    STATUS_EXPIRED,
    STATUS_INACTIVE,
  ].includes(currentStatus);
  const disabledGGCampaignsField =
    disabledField || !values[APPLICATION] || !values[AFFILIATE];

  const typeId = values[ACTIVITY_TYPE];
  const types = hierarchyState?.[ACTIVITY_TYPE]?.data;
  const {
    ggCampaignRequired = true,
    includeReAttribution = false,
    isCrm,
  } = useMemo(
    () => types?.find((type) => type.id === typeId) || {},
    [types, typeId]
  );

  const activeGGCampaignsField = Boolean(
    values[APPLICATION] && values[AFFILIATE]
  );
  const isDisabledExcludedCountries = useMemo(
    () => disabledField || !!values[COUNTRIES]?.length,
    [values[COUNTRIES], disabledField]
  );
  const isDisabledCountries = useMemo(
    () => disabledField || !!values[EXCLUDED_COUNTRIES]?.length,
    [values[EXCLUDED_COUNTRIES], disabledField]
  );

  const isMultiRedemption = values[MULTI_REDEMPTION];

  const { data: rewards, loading: rewardsLoading } = useHierarchyArea({
    depends: DEPENDENCIES[REWARDS],
    source: loadRewards,
    name: REWARDS,
  });

  const { data: conditions, loading: conditionsLoading } = useHierarchyArea({
    depends: DEPENDENCIES[REWARDS],
    source: loadConditions,
    name: CONDITIONS,
  });

  const handlePromoCodeValidate = async (value) => {
    if (value) {
      const matchLength =
        value.length >= 4 && value.length <= (isSetFieldVisible ? 18 : 25);
      const matchSymbols = value?.match(/^[a-zA-Z0-9_.-]*$/g);

      if (!(matchLength && matchSymbols)) {
        return `4 - ${isSetFieldVisible ? 18 : 25} numbers or letters`;
      } else {
        const validCode = await validatePromoCode({ id: values.id, value });
        return !validCode ? 'This code already in use, try another' : false;
      }
    }
  };

  const validateHandleOnBlur = (e) => {
    if (e.target.value) {
      validateField(PROMO_CODE);
    }
  };

  const handleValidateGoGameCampaigns = (value) => {
    if (ggCampaignRequired && !isCrm && !value?.length) {
      return 'At least one campaign is required';
    }
  };

  const handleValidateEndDate = (date) => {
    if (!date) {
      return 'Expiration date is required';
    }
  };

  const handleTypeChange = (e, value, option) => {
    setValues({
      ...values,
      [e.target.name]: value,
      ...(option.ggCampaignRequired
        ? {}
        : {
            [AFFILIATE]: undefined,
          }),

      ...(option.includeReAttribution
        ? {}
        : {
            [INCLUDE_RE_ATTR]: undefined,
          }),

      ...(option.isCrm
        ? {
            [AFFILIATE]: undefined,
            [CAMPAIGNS]: undefined,
            [COUNTRIES]: undefined,
            [EXCLUDED_COUNTRIES]: undefined,
          }
        : {
            [MULTI_REDEMPTION]: undefined,
            [MAX_REDEMPTIONS_PER_USER]: undefined,
            [MULTI_REDEMPTION_ACTIVITY_PERIOD]: undefined,
            [MULTI_REDEMPTION_COOLDOWN_PERIOD]: undefined,
          }),
    });
  };

  const handleChangeMultipleRedemption = (_, v) => {
    setValues({
      ...values,
      [MULTI_REDEMPTION]: v,
      ...(!v
        ? {
            [MAX_REDEMPTIONS_PER_USER]: undefined,
            [MULTI_REDEMPTION_ACTIVITY_PERIOD]: undefined,
            [MULTI_REDEMPTION_COOLDOWN_PERIOD]: undefined,
          }
        : null),
    });
  };

  const loadTypesByPromoCode = React.useCallback(
    (params) => {
      return loadTypes({
        ...params,
        promoCodeType: values.isSet ? 'SetOfPersonalCodes' : 'PublicCode',
      });
    },
    [values.isSet]
  );

  return (
    <SidePageV2
      onClose={onClose}
      onExited={onExited}
      open={open}
      loading={loading}
      size={'lg'}
    >
      <SidePageV2.Title title={title} subTitle={subTitle} entityId={entityId} />
      <SidePageV2.NewBody>
        <Section title={'General'}>
          <Grid container spacing={3}>
            <Grid item xs={isSetFieldVisible ? 6 : 8}>
              <FormField.TextField
                label={'Name'}
                name={NAME}
                padding={'0'}
                required
              />
            </Grid>
            <Grid item xs={4}>
              <FormField.TextField
                label={'Code'}
                name={PROMO_CODE}
                placeholder={`4 - ${
                  isSetFieldVisible ? 18 : 25
                } numbers or letters`}
                padding={'0'}
                onBlur={validateHandleOnBlur}
                validate={handlePromoCodeValidate}
                disabled={disabledField}
                required
              />
            </Grid>
            {isSetFieldVisible && (
              <Grid item xs={2}>
                <FormField.TextField
                  label={'Number'}
                  name={NUMBER}
                  type={'number'}
                  padding={'0'}
                  required
                />
              </Grid>
            )}
            <Grid item xs={4}>
              <FormField.Select
                label={'Requested by'}
                name={REQUESTED_BY}
                source={loadUsers}
                padding={'0'}
                required
              />
            </Grid>
            <Grid item xs={8}>
              <FormField.TextField
                label={'Jira task URL'}
                name={JIRA_LINK}
                padding={'0'}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormField.TextField
                label={'Description'}
                name={COMMENT}
                multiline={true}
                padding={'0'}
              />
            </Grid>
          </Grid>
        </Section>
        <Section title={'Population'}>
          <Grid container item spacing={3}>
            <Grid item xs={6}>
              <MultiChangeSelect
                label={'Activity type'}
                name={ACTIVITY_TYPE}
                source={loadTypesByPromoCode}
                padding={'0'}
                depends={DEPENDENCIES[ACTIVITY_TYPE]}
                disabled={disabledField}
                onMultiChange={handleTypeChange}
                required
              />
            </Grid>
            <Grid item xs={6}>
              {isCrm ? (
                <Checkbox
                  label={'Multiple redemptions'}
                  name={MULTI_REDEMPTION}
                  checked={values[MULTI_REDEMPTION]}
                  onChange={handleChangeMultipleRedemption}
                />
              ) : (
                <FormField.Checkbox
                  label={'Include RE-attribution'}
                  padding={0}
                  name={INCLUDE_RE_ATTR}
                  disabled={!includeReAttribution}
                />
              )}
            </Grid>
            {isMultiRedemption ? (
              <React.Fragment>
                <Grid item xs={6}>
                  <FormField.TextField
                    label={'Max redemptions per player'}
                    name={MAX_REDEMPTIONS_PER_USER}
                    padding={'0'}
                    type={'number'}
                    validate={getOneOf(
                      getRequiredValidate('Max redemptions per player'),
                      getLessThanValidate('Max redemptions per player', 2)
                    )}
                    required
                  />
                </Grid>
                <Grid item xs={3}>
                  <FormField.TextField
                    label={'Cooldown days'}
                    name={MULTI_REDEMPTION_COOLDOWN_PERIOD}
                    padding={'0'}
                    type={'number'}
                    validate={getOneOf(
                      getRequiredValidate('Cooldown days'),
                      getGrateThanValidate(
                        'Cooldown days',
                        values[MULTI_REDEMPTION_ACTIVITY_PERIOD],
                        'activity days'
                      )
                    )}
                    required
                  />
                </Grid>

                <Grid item xs={3}>
                  <FormField.TextField
                    label={'Activity days'}
                    name={MULTI_REDEMPTION_ACTIVITY_PERIOD}
                    padding={'0'}
                    type={'number'}
                    validate={getOneOf(
                      getRequiredValidate('Activity days'),
                      getGrateThanValidate('Activity days', 0)
                    )}
                    required
                  />
                </Grid>
              </React.Fragment>
            ) : null}

            <Grid item xs={6}>
              <FormField.Select
                label={'Application'}
                name={APPLICATION}
                source={loadApplications}
                padding={'0'}
                disabled={disabledField}
                required
              />
            </Grid>
            <Grid item xs={6}>
              <FormField.MultiSelect
                label={'Platforms'}
                name={PLATFORMS}
                source={loadPlatforms}
                padding={'0'}
                depends={DEPENDENCIES[PLATFORMS]}
                disabled={disabledField}
                withAll={true}
              />
            </Grid>
            {!isCrm ? (
              <React.Fragment>
                <Grid item xs={6}>
                  <FormField.Select
                    label={'Affiliate'}
                    name={AFFILIATE}
                    source={loadAffiliate}
                    padding={'0'}
                    disabled={disabledField || !ggCampaignRequired}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormField.MultiSelect
                    label={'GG Campaigns'}
                    name={CAMPAIGNS}
                    source={loadCampaigns}
                    padding={'0'}
                    depends={DEPENDENCIES[CAMPAIGNS]}
                    disabled={disabledGGCampaignsField}
                    active={activeGGCampaignsField}
                    validate={handleValidateGoGameCampaigns}
                    required={ggCampaignRequired}
                    getOptionLabel={getOptionNameAndId}
                    displayTooltip={true}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormField.MultiSelect
                    label={'Countries'}
                    name={COUNTRIES}
                    source={loadCountries}
                    padding={'0'}
                    depends={DEPENDENCIES[COUNTRIES]}
                    disabled={isDisabledCountries}
                    placeholder={'All'}
                    withAll={true}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormField.MultiSelect
                    label={'Exclude Countries'}
                    name={EXCLUDED_COUNTRIES}
                    source={loadCountries}
                    padding={'0'}
                    depends={DEPENDENCIES[COUNTRIES]}
                    disabled={isDisabledExcludedCountries}
                  />
                </Grid>
              </React.Fragment>
            ) : null}
          </Grid>
        </Section>
        <Section title={'Activity'}>
          <Grid container spacing={3}>
            <Grid item xs={3}>
              <FormField.DatePicker
                label={'Start date'}
                name={START_DATE}
                padding={'0'}
                required
              />
            </Grid>
            {isPublicFieldVisible && active && (
              <DeactivateUponSelect values={values} setValues={setValues} />
            )}
            {isSetFieldVisible && (
              <Grid item xs={3}>
                <FormField.DatePicker
                  label={'Expiration date'}
                  name={FINISH_DATE}
                  padding={'0'}
                  required
                  validate={handleValidateEndDate}
                />
              </Grid>
            )}
            {isSetFieldVisible && (
              <Grid item xs={12}>
                <Alert>Usage limit for each promo code is 1</Alert>
              </Grid>
            )}
            {isMultiRedemption && (
              <Grid item xs={12}>
                <Alert>
                  The expiration date is automatically calculated using the
                  values from 'Max redemptions per player', 'Cooldown days' and
                  'Start date'.
                </Alert>
              </Grid>
            )}
          </Grid>
        </Section>
        <Section title={'Reward'}>
          <Rewards
            name={REWARDS}
            data={rewards}
            disabled={!values[APPLICATION]}
            loading={rewardsLoading}
          />
        </Section>
        <Section title={'Conditional Rewards'}>
          <ConditionalRewards
            name={CONDITIONS}
            conditions={conditions}
            rewards={rewards}
            values={values}
            loading={rewardsLoading || conditionsLoading}
          />
        </Section>
      </SidePageV2.NewBody>
      <SidePageV2.Actions>
        <SidePageV2.CloseButton />
        <SidePageV2.SubmitButton />
      </SidePageV2.Actions>
    </SidePageV2>
  );
});

export default PromoCodeForm;

function getOneOf(...fns) {
  return (...args) => {
    return fns.map((fn) => fn(...args)).find((v) => v);
  };
}

function getLessThanValidate(valueLabel, compareValue, compareLabel) {
  return (v) => {
    if (v < compareValue) {
      return `${valueLabel} is less than ${compareLabel || compareValue}`;
    }
  };
}

function getGrateThanValidate(valueLabel, compareValue, compareLabel) {
  return (v) => {
    if (v <= compareValue) {
      return `${valueLabel} should be greater than ${compareLabel || compareValue}`;
    }
  };
}

function getRequiredValidate(label) {
  return (v) => {
    if (v === undefined) {
      return `${label} is required`;
    }
  };
}
